import { ValidationSchema } from 'forms';
import {
  ComponentModule,
  ListBonusComponentsInput,
  PaymentSource,
  PaymentSourceType,
  TaxDeductionBehaviour,
  UpdateBonusAssignmentInput,
} from 'api/gql-utils/bonusAssignment/types';
import { TaxDeductionBehaviour as TaxDeductionBehaviourType } from 'components/SalaryComponents/types';
import { BonusFormData, PaymentStatusOption } from './types';

export const initialBonusFormData: BonusFormData = {
  employeeName: '',
  peopleId: '',
  userOrgId: '',
  assignmentId: '',
  bonusType: '',
  bonusAmount: '',
  initialBonusAmount: '',
  paymentStatus: '',
  initialPaymentStatus: '',
  payoutMonth: '',
  initialPayoutMonth: '',
  remark: '',
  hasClawback: 'false',
  clawbackMonths: '',
};

export const bonusSteps = {
  step1: 0,
  step2: 1,
  step3: 2,
};

export const TOTAL_STEPS = 3;

export const REARCH_BONUS_TYPES_QUERY_KEY = 'GET_REARCH_BONUS_TYPES';

export const BONUS_COMPONENTS_PAGE_LIMIT = 100;

export const BONUS_ASSIGNMENTS_PAGE_LIMIT = 100;

export const REARCH_BONUS_TYPES_QUERY_VARIABLES: ListBonusComponentsInput = {
  category: 'BONUS_CATEGORY',
  type: ComponentModule.BONUS,
  status: 'SALARY_COMPONENT_STATUS_ACTIVE',
  limit: BONUS_COMPONENTS_PAGE_LIMIT,
  offset: 0,
};

export const REARCH_BONUS_COMPONENT_NAMES_QUERY_VARIABLES: ListBonusComponentsInput = {
  category: 'BONUS_CATEGORY',
  type: ComponentModule.BONUS,
  limit: BONUS_COMPONENTS_PAGE_LIMIT,
  offset: 0,
};

export const PAISE_PER_RUPEE = 100;

export const PAYOUT_MONTHS_AHEAD = 12;
export const PAYOUT_MONTHS_BEHIND = 12;

export const PAYOUT_MONTH_HELP_TEXT: Record<string, string> = {
  to_be_paid:
    "Choose the payroll month for bonus payments. We will automatically include the bonus in that month's payroll and notify you",
  already_paid:
    'Choose the payroll month this bonus is attributed to for TDS. It was paid outside XPayroll, so it will not be added to payroll.',
};

export const PAYMENT_STATUS_OPTIONS: PaymentStatusOption[] = [
  { value: 'to_be_paid', title: 'To be paid via XPayroll' },
  { value: 'already_paid', title: 'Already paid' },
];

export const PAYMENT_STATUS_TO_PAYMENT_SOURCE: Record<string, PaymentSourceType> = {
  to_be_paid: PaymentSource.WITHIN_PAYROLL,
  already_paid: PaymentSource.ALREADY_PAID,
};

export const PAYMENT_STATUS_TO_DISBURSEMENT_STATUS: Record<
  string,
  UpdateBonusAssignmentInput['payment_status']
> = {
  to_be_paid: 'PAYMENT_STATUS_PENDING',
  already_paid: 'PAYMENT_STATUS_PAID',
};

export const TDS_SETTING_LABELS: Record<TaxDeductionBehaviourType, string> = {
  [TaxDeductionBehaviour.INSTANT]: 'Instant Deduction',
  [TaxDeductionBehaviour.PRO_RATED]: 'Prorated Deduction',
  [TaxDeductionBehaviour.NOT_APPLICABLE]: 'Not Applicable',
};

export const getTdsSettingLabel = (behaviour?: TaxDeductionBehaviourType | null) =>
  behaviour ? TDS_SETTING_LABELS[behaviour] : undefined;

export const PAYROLL_FINALIZED_ERROR =
  'Payroll for this month has already been finalised. Choose a later payout month.';
export const PAYROLL_BLOCKED_ERROR =
  'Payroll for this month is currently being processed and cannot accept new inputs. Choose a later payout month.';
export const PAYROLL_STATUS_CHECK_FAILED_ERROR =
  'Could not verify the payroll status for this month. Please try again.';

export const MIN_BONUS_AMOUNT_RUPEES = 1;
export const MAX_BONUS_AMOUNT_RUPEES = Math.floor(Number.MAX_SAFE_INTEGER / PAISE_PER_RUPEE);

export const MAX_CLAWBACK_MONTHS = 120;

export const bonusDetailsValidationSchema: ValidationSchema<BonusFormData> = {
  bonusType: (formData) => (!formData.bonusType ? 'Please choose a bonus' : null),
  bonusAmount: (formData) => {
    if (!formData.bonusAmount) return 'Bonus amount is required';
    const amount = Number(formData.bonusAmount);
    if (!Number.isFinite(amount)) return 'Enter a valid bonus amount';
    if (amount < MIN_BONUS_AMOUNT_RUPEES) return 'Bonus amount must be greater than zero';
    if (amount > MAX_BONUS_AMOUNT_RUPEES) return 'Bonus amount is too large';
    return null;
  },
  paymentStatus: (formData) => (!formData.paymentStatus ? 'Please select payment status' : null),
  payoutMonth: (formData) => (!formData.payoutMonth ? 'Please choose payout month' : null),
};

export const clawbackValidationSchema: ValidationSchema<BonusFormData> = {
  clawbackMonths: (formData) => {
    if (formData.hasClawback !== 'true') return null;
    if (!formData.clawbackMonths) return 'Number of months is required';
    const months = Number(formData.clawbackMonths);
    if (!Number.isInteger(months) || months < 1) {
      return 'Enter the clawback duration as a whole number of months';
    }
    if (months > MAX_CLAWBACK_MONTHS) {
      return `Clawback duration cannot exceed ${MAX_CLAWBACK_MONTHS} months`;
    }
    return null;
  },
};
